/* ==========================================================================
   Rook UI Core — Dialog Controller
   Web Component: <rk-dialog-element>
   ========================================================================== */

if (!customElements.get('rk-dialog-element')) {
    class RkDialogElement extends HTMLElement {
        constructor() {
            super();
            this.dialog = null;
            this.overlay = null;
            this.content = null;
            this.previousFocus = null;

            this._onKeyDown = this._onKeyDown.bind(this);
        }

        connectedCallback() {
            this.dialog = this.querySelector('.rk-dialog');
            this.overlay = this.querySelector('.rk-dialog__overlay');
            this.content = this.querySelector('.rk-dialog__content');

            if (!this.dialog) return;

            this._bindEvents();
            this._setupOpenTriggers();
        }

        /* ------------------------------------------------------------------ */
        /*  Events                                                            */
        /* ------------------------------------------------------------------ */

        _bindEvents() {
            // Close button
            this.querySelectorAll('[data-action="close"]').forEach((el) => {
                el.addEventListener('click', () => this.close());
            });

            // Overlay click (configurable)
            if (this.overlay) {
                const overlayClose = this.dataset.overlayClose !== 'false';
                if (overlayClose) {
                    this.overlay.addEventListener('click', () => this.close());
                }
            }
        }

        _setupOpenTriggers() {
            const dialogId = this.dataset.dialogId || this.dialog?.id;
            if (!dialogId) return;

            document.querySelectorAll(`[data-dialog-open="${dialogId}"]`).forEach((trigger) => {
                trigger.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.open();
                });
            });
        }

        /* ------------------------------------------------------------------ */
        /*  Public API                                                        */
        /* ------------------------------------------------------------------ */

        isOpen() {
            return this.dialog?.classList.contains('rk-dialog--active');
        }

        open() {
            if (!this.dialog || this.isOpen()) return;

            this.previousFocus = document.activeElement;
            this.dialog.classList.add('rk-dialog--active');
            document.body.style.overflow = 'hidden';
            document.addEventListener('keydown', this._onKeyDown);

            // Focus first interactive element inside content
            requestAnimationFrame(() => {
                const firstFocusable = this.content?.querySelector(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                );
                if (firstFocusable) {
                    firstFocusable.focus();
                }
            });

            this.dispatchEvent(
                new CustomEvent('rk:dialog:open', {
                    bubbles: true,
                    detail: { id: this.dialog.id },
                })
            );
        }

        close() {
            if (!this.dialog || !this.isOpen()) return;

            this.dialog.classList.remove('rk-dialog--active');
            document.body.style.overflow = '';
            document.removeEventListener('keydown', this._onKeyDown);

            if (this.previousFocus) {
                this.previousFocus.focus();
                this.previousFocus = null;
            }

            this.dispatchEvent(
                new CustomEvent('rk:dialog:close', {
                    bubbles: true,
                    detail: { id: this.dialog.id },
                })
            );
        }

        /* ------------------------------------------------------------------ */
        /*  Keyboard handling (ESC + Tab trap)                                */
        /* ------------------------------------------------------------------ */

        _onKeyDown(e) {
            if (!this.isOpen()) return;

            // ESC to close
            if (e.key === 'Escape') {
                e.preventDefault();
                this.close();
                return;
            }

            // Tab trap
            if (e.key === 'Tab') {
                this._trapFocus(e);
            }
        }

        _trapFocus(e) {
            const focusable = this.content?.querySelectorAll(
                'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );

            if (!focusable || focusable.length === 0) return;

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (e.shiftKey) {
                // Shift+Tab: if focus is on first, wrap to last
                if (document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else {
                // Tab: if focus is on last, wrap to first
                if (document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        }
    }

    customElements.define('rk-dialog-element', RkDialogElement);
}
